import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Apollo, gql } from 'apollo-angular';
import { EMPTY, Observable, Subject, reduce } from 'rxjs';
import { expand, filter, map, tap } from 'rxjs/operators';
import { GqlQueryResult, PERSON_ROLES } from '.';
import { FlyFreelyConstants } from '../constants';
import {
    CreatePersonOrganisationCommand,
    CurrencyResultDto,
    FlightConformancePolicyDto,
    FlightConformanceResultDto,
    HistoricalPersonDto,
    OrganisationIdentifierDto,
    PersonDto,
    PersonRolesDto,
    PersonnelRegisterEntryDetailsDto,
    PersonsOrganisationDto,
    PilotFlightAggregatesDto,
    PilotFlightHistoryDto,
    PilotMissionFlightSummary,
    SimpleWorkgroupDto,
    SortieDto,
    UpdatePersonOrganisationCommand,
    UpdatePersonnelRolesCommand
} from '../model/api';
import { FlightHistoryEntry } from './flightHistory';
import { httpParamSerializer } from './service.helpers';

export interface PilotFlightHistoryWithConformance
    extends PilotFlightHistoryDto {
    flights: PilotFlightSummaryWithConformance[];
}

export interface PilotFlightSummaryWithConformance
    extends PilotMissionFlightSummary {
    conformanceResultList: PilotFlightConformanceResult[];
}

export interface PilotFlightConformanceResult
    extends FlightConformanceResultDto {
    policy?: FlightConformancePolicyDto;
    pilot?: PersonDto;
    flight?: SortieDto;
}

export interface PilotFlightHistoryEntry extends FlightHistoryEntry {
    conformanceResultList: PilotFlightConformanceResult[];
}

export interface PersonServiceChanges {
    personnel: PersonRolesDto[];
    historicalPersonnel: HistoricalPersonDto[];
}

export interface FindSortieResult {
    count: number;
    results: SortieDto[];
}

export interface PersonWithRoles {
    id: number;
    firstName: string;
    lastName: string;
    email: string;
    phoneNumber?: string;
    roles: PersonRolesDto.Roles[];
    workgroupList: SimpleWorkgroupDto[];
    active: boolean;
    configuredIntegrations: {
        integration: OrganisationIdentifierDto.Integration;
        configured: boolean;
    }[];
}

export interface FindPersonnelResult {
    count: number;
    results: PersonWithRoles[];
}

export interface PersonnelCriteria {
    organisationId: number;
    textSearch?: string;
    firstName?: string;
    lastName?: string;
    email?: string;
    roleOrList?: PersonRolesDto.Roles[];
    idList?: number[];
    excludedIdList?: number[];
}

export enum PersonnelFields {
    FIRST_NAME = 'firstName',
    LAST_NAME = 'lastName',
    EMAIL = 'email'
}

export interface PersonnelFieldSort {
    field: PersonnelFields;
    order: 'ASC' | 'DESC';
}

@Injectable({
    providedIn: 'root'
})
export class PersonService {
    currentValues: PersonServiceChanges = {
        personnel: [],
        historicalPersonnel: []
    };

    private baseUrl: string;
    private changeSource = new Subject<void>();
    private valuesSource = new Subject<PersonServiceChanges>();
    change$ = this.changeSource.asObservable();
    values$ = this.valuesSource.asObservable();

    constructor(
        private apollo: Apollo,
        constants: FlyFreelyConstants,
        private http: HttpClient
    ) {
        this.baseUrl = constants.API_URL;

        this.values$.subscribe(changes => (this.currentValues = changes));
    }

    ngOnDestroy() {
        this.changeSource.complete();
    }

    markAsChanged() {
        this.changeSource.next();
    }

    findPersonnel(organisationId: number, role?: PersonRolesDto.Roles) {
        return this.http
            .get<PersonRolesDto[]>(`${this.baseUrl}/webapi/personnel`, {
                params: httpParamSerializer({ organisationId, role })
            })
            .pipe(
                tap(personnel =>
                    this.valuesSource.next({
                        ...this.currentValues,
                        personnel: personnel
                    })
                )
            );
    }

    findPersonnelGraphQL(
        organisationId: number,
        page: number,
        pageSize: number,
        criteria?: Partial<PersonnelCriteria>,
        sortFields?: PersonnelFieldSort[]
    ) {
        return this.apollo
            .query<{
                findPersonnel: FindPersonnelResult;
            }>({
                query: gql`
                    query findPersonnel(
                        $criteria: PersonnelCriteria!
                        $sortFields: [PersonnelFieldSort!]
                        $page: Int
                        $pageSize: Int
                    ) {
                        findPersonnel(
                            criteria: $criteria
                            sortFields: $sortFields
                            page: $page
                            pageSize: $pageSize
                        ) {
                            count
                            results {
                                id
                                firstName
                                lastName
                                email
                                phoneNumber
                                roles
                                workgroupList {
                                    id
                                    name
                                    archived
                                }
                                active
                                configuredIntegrations {
                                    integration
                                    configured
                                }
                            }
                        }
                    }
                `,
                variables: {
                    criteria: {
                        organisationId,
                        ...criteria
                    },
                    sortFields,
                    page,
                    pageSize
                },
                fetchPolicy: 'network-only'
            })
            .pipe(
                filter(r => !r.loading),
                map(r => r.data.findPersonnel)
            );
    }

    findAllPersonnelGraphQL(
        organisationId: number
    ): Observable<PersonWithRoles[]> {
        const pageSize = 100;
        return this.findPersonnelGraphQL(organisationId, 0, pageSize).pipe(
            expand((result, index) => {
                const currentPage = index + 1;
                const totalPages = Math.ceil(result.count / pageSize);

                if (currentPage >= totalPages) {
                    return EMPTY;
                }

                return this.findPersonnelGraphQL(
                    organisationId,
                    currentPage,
                    pageSize
                );
            }),
            reduce(
                (acc, result) => [...acc, ...result.results],
                [] as PersonWithRoles[]
            ),
            tap(allPersonnel => {
                const personnel = allPersonnel as unknown as PersonRolesDto[];
                this.valuesSource.next({
                    ...this.currentValues,
                    personnel
                });
            })
        );
    }

    findPersonnelWithPermission(
        organisationId: number,
        permission: PersonsOrganisationDto.Permissions
    ) {
        return this.http
            .get<PersonRolesDto[]>(`${this.baseUrl}/webapi/personnel`, {
                params: httpParamSerializer({ organisationId, permission })
            })
            .pipe(
                tap(personnel =>
                    this.valuesSource.next({
                        ...this.currentValues,
                        personnel: personnel
                    })
                )
            );
    }

    findHistoricalPersonnel(organisationId: number) {
        return this.http
            .get<HistoricalPersonDto[]>(
                `${this.baseUrl}/webapi/personnel/historical`,
                {
                    params: httpParamSerializer({ organisationId })
                }
            )
            .pipe(
                tap(historicalPersonnel =>
                    this.valuesSource.next({
                        ...this.currentValues,
                        historicalPersonnel: historicalPersonnel
                    })
                )
            );
    }

    findRelatedPersonnel(organisationId: number) {
        return this.http.get<PersonDto[]>(
            `${this.baseUrl}/webapi/personnel/all`,
            {
                params: httpParamSerializer({ organisationId })
            }
        );
    }

    findPerson(personId: number, organisationId: number) {
        return this.http.get<PersonRolesDto>(
            `${this.baseUrl}/webapi/personnel/${personId}`,
            {
                params: httpParamSerializer({ organisationId })
            }
        );
    }

    createPerson(command: CreatePersonOrganisationCommand) {
        return this.http
            .post<PersonDto>(`${this.baseUrl}/webapi/personnel`, command)
            .pipe(tap(() => this.changeSource.next()));
    }

    updatePerson(personId: number, command: UpdatePersonOrganisationCommand) {
        return this.http
            .put<PersonDto>(
                `${this.baseUrl}/webapi/personnel/${personId}`,
                command
            )
            .pipe(tap(() => this.changeSource.next()));
    }

    updateRoles(
        personId: number,
        personOrganisation: UpdatePersonnelRolesCommand
    ) {
        return this.http
            .put(
                `${this.baseUrl}/webapi/personnel/${personId}/roles`,
                personOrganisation
            )
            .pipe(tap(() => this.changeSource.next()));
    }

    findPilotFlightHistory(
        criteria: {
            id: number;
            managingOrganisationId: number;
            conformanceResultStatus?: FlightConformanceResultDto.Status[];
        },
        order: 'ASC' | 'DESC',
        page = 0,
        pageSize = 10
    ) {
        return this.apollo
            .query<{
                findPilotFlightHistory: GqlQueryResult<PilotFlightHistoryEntry>;
            }>({
                query: gql`
                    query find(
                        $criteria: PilotFlightHistoryCriteria
                        $page: Int
                        $pageSize: Int
                        $order: Direction
                    ) {
                        findPilotFlightHistory(
                            criteria: $criteria
                            order: $order
                            page: $page
                            pageSize: $pageSize
                        ) {
                            count
                            results {
                                id
                                type
                                missionId
                                flightNumber
                                numberOfFlights
                                startTime
                                endTime
                                duration
                                visualLineOfSight
                                locationName
                                timeOfDay
                                rpaId
                                rpaType {
                                    id
                                    make
                                    model
                                    rpaCategory
                                    performanceSpecifications {
                                        maxTakeOffWeight
                                    }
                                }
                                conformanceResultList {
                                    status
                                }
                            }
                        }
                    }
                `,
                variables: {
                    criteria,
                    order,
                    page,
                    pageSize
                },
                fetchPolicy: 'network-only'
            })
            .pipe(
                filter(r => !r.loading),
                map(r => ({
                    ...r.data.findPilotFlightHistory,
                    pagination: { page, pageSize }
                }))
            );
    }

    exportOrganisationPersonnel(
        organisationId: number,
        includeRoles: boolean,
        authorityTypeId?: number[]
    ) {
        const headers = new HttpHeaders({
            'Content-type': 'text/csv;charset=utf-8',
            Accept: 'text/csv;charset=utf-8'
        });

        return this.http.get(`${this.baseUrl}/webapi/personnel/export`, {
            params: httpParamSerializer({
                organisationId,
                includeRoles,
                authorityTypeId
            }),
            responseType: 'blob',
            headers: headers
        });
    }

    findCurrencyResults(
        personId: number,
        personnelRegisterId: number,
        managingOrganisationId: number
    ) {
        return this.http.get<CurrencyResultDto[]>(
            `${this.baseUrl}/webapi/personnel/${personId}/currency`,
            {
                params: httpParamSerializer({
                    personnelRegisterId,
                    managingOrganisationId
                })
            }
        );
    }

    findFlightStatisticsBrief(
        personId: number,
        managingOrganisationId?: number
    ) {
        return this.http.get<PilotFlightAggregatesDto>(
            `${this.baseUrl}/webapi/personnel/${personId}/history/summary`,
            { params: httpParamSerializer({ managingOrganisationId }) }
        );
    }

    getProfilePhotoUrl(
        personId: number,
        managingOrganisationId: number
    ): string {
        return `/webapi/personnel/${personId}/logo?managingOrganisationId=${managingOrganisationId}`;
    }

    findPersonnelRegisterEntryByPersonId(
        personId: number,
        managingOrganisationId: number,
        at: string
    ) {
        return this.http.get<PersonnelRegisterEntryDetailsDto[]>(
            `${this.baseUrl}/webapi/personnel/${personId}/authorityRegisters`,
            { params: httpParamSerializer({ at, managingOrganisationId }) }
        );
    }

    getPersonRoles() {
        return PERSON_ROLES;
    }
}
